import type { Restaurant, ThemeKey } from '@cartazo/contracts'

/**
 * Aplicación del tema y de los colores de marca del local.
 *
 * Precedencia, de menor a mayor:
 *   tema base del local → overrides de marca → ?theme= (previsualización del
 *   panel) → elección guardada del comensal.
 *
 * La elección del comensal gana siempre: si alguien prefiere el tema claro
 * porque está en una terraza a pleno sol, ninguna preferencia del restaurante
 * debe revertirla.
 */

const THEME_KEY = 'cartazo:theme'
export const THEMES: ThemeKey[] = ['terral', 'kawaii', 'neon', 'botanic', 'cantina']

export function applyBrand(restaurant: Restaurant): void {
  const root = document.documentElement
  const theme = resolveTheme(restaurant.theme_default)

  root.setAttribute('data-theme', theme)

  // Limpia los overrides de una aplicación anterior: sin esto, cambiar de tema
  // arrastraría los colores del anterior por ser estilos inline.
  for (const token of BRAND_TOKENS) root.style.removeProperty(token)

  /**
   * Los colores de marca SOLO se aplican sobre el tema propio del restaurante.
   *
   * Si el comensal eligió otro tema, la paleta del local deja de tener sentido:
   * un acento naranja pensado para un fondo oscuro aterriza sobre el rosa de
   * `kawaii` y el resultado no es la marca del local, es un accidente. Peor,
   * hace que los cuatro temas se vean iguales y el selector parezca roto.
   *
   * La tipografía sí se conserva: es identidad de marca y no depende de la
   * paleta.
   */
  const brand = restaurant.brand
  const usingOwnTheme = theme === restaurant.theme_default

  if (brand.font_heading) root.style.setProperty('--t-font-heading', brand.font_heading)
  if (brand.font_body) root.style.setProperty('--t-font-body', brand.font_body)
  if (brand.radius) root.style.setProperty('--t-radius', brand.radius)

  if (!usingOwnTheme) return

  // Nunca fondo ni color de texto: dejar que el local los toque permitiría que
  // su propia carta acabara siendo ilegible.
  if (brand.primary) root.style.setProperty('--t-primary', brand.primary)
  if (brand.accent) root.style.setProperty('--t-accent', brand.accent)

  // Guardarraíl de contraste: si el color sobre el acento no se lee, se corrige
  // solo. Un botón "Agregar al pedido" ilegible cuesta ventas reales.
  const accent = brand.accent ?? readVar('--t-accent')
  const onAccent = brand.on_accent ?? readVar('--t-on-accent')
  root.style.setProperty('--t-on-accent', ensureReadable(accent, onAccent))
}

/** Tokens que `applyBrand` puede sobrescribir, y por tanto debe poder limpiar. */
const BRAND_TOKENS = [
  '--t-primary',
  '--t-accent',
  '--t-on-accent',
  '--t-font-heading',
  '--t-font-body',
  '--t-radius',
] as const

export function resolveTheme(fallback: string): string {
  const params = new URLSearchParams(window.location.search)
  const preview = params.get('theme')
  if (preview && THEMES.includes(preview as ThemeKey)) return preview

  try {
    const chosen = localStorage.getItem(THEME_KEY)
    if (chosen && THEMES.includes(chosen as ThemeKey)) return chosen
  } catch {
    // Sin almacenamiento, se usa el tema del local.
  }

  return THEMES.includes(fallback as ThemeKey) ? fallback : 'terral'
}

export function setTheme(theme: ThemeKey): void {
  document.documentElement.setAttribute('data-theme', theme)
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    // El tema no persistirá entre visitas, pero se aplica en esta.
  }
}

function readVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/**
 * Devuelve un color de texto legible sobre `background`.
 *
 * Usa luminancia relativa (WCAG) en vez de una heurística sobre el tono: un
 * amarillo saturado y un azul saturado tienen luminancias opuestas aunque el
 * tono no lo sugiera.
 */
function ensureReadable(background: string, preferred: string): string {
  const bg = parseColor(background)
  if (!bg) return preferred

  const preferredRgb = parseColor(preferred)
  if (preferredRgb && contrastRatio(bg, preferredRgb) >= 4.5) return preferred

  // 4.5:1 es el mínimo de WCAG AA para texto normal.
  const white = { r: 255, g: 255, b: 255 }
  const black = { r: 0, g: 0, b: 0 }
  return contrastRatio(bg, white) >= contrastRatio(bg, black) ? '#ffffff' : '#000000'
}

type Rgb = { r: number; g: number; b: number }

function parseColor(value: string): Rgb | null {
  const hex = value.trim().replace('#', '')

  // Forma corta (#f0a): cada dígito se duplica.
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    const expanded = hex.replace(/./g, (digit) => digit + digit)
    return {
      r: Number.parseInt(expanded.slice(0, 2), 16),
      g: Number.parseInt(expanded.slice(2, 4), 16),
      b: Number.parseInt(expanded.slice(4, 6), 16),
    }
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return {
      r: Number.parseInt(hex.slice(0, 2), 16),
      g: Number.parseInt(hex.slice(2, 4), 16),
      b: Number.parseInt(hex.slice(4, 6), 16),
    }
  }

  const rgb = value.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i)
  if (rgb) {
    return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) }
  }

  return null
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const v = value / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}
